/** @format */
import { css } from 'styled-components';

import { mobile } from '@toucan/client/global/application';

import {
    ScrollableCarouselButtonSize,
    StyledScrollableCarouselProps,
} from './index';

export const GRADIENT_WIDTH_DESKTOP = 140;
export const GRADIENT_WIDTH_MOBILE = 48;
export const SCROLLBAR_HEIGHT = '20px';

/**
 * Base styles for the ScrollableCarousel component
 */
const baseStyles = ({
    paddingLeft = 0,
    showNextButton,
    showPrevButton,
}: StyledScrollableCarouselProps) => {
    return css`
        overflow: hidden;
        position: relative;

        &::before,
        &::after {
            content: '';
            display: block;
            pointer-events: none;
            width: ${GRADIENT_WIDTH_DESKTOP / 10}rem;
            position: absolute;
            top: 0;
            bottom: 0;
            z-index: 2;
            transition: opacity 0.3s ease-in-out;
        }

        &::before {
            opacity: ${showPrevButton ? 1 : 0};
            left: 0;
            background: var(--gradients-surface-fade-left);
        }

        &::after {
            opacity: ${showNextButton ? 1 : 0};
            right: 0;
            background: var(--gradients-surface-fade-right);
        }

        .scrollable-carousel-outer {
            overflow-x: auto;
            overflow-y: hidden;
            position: relative;

            /* Used to help hide the scrollbar */
            margin-bottom: -${SCROLLBAR_HEIGHT};
            padding-left: ${paddingLeft}px;
            scrollbar-width: none;
        }

        .scrollable-carousel-inner {
            padding: 1rem 0;
            display: inline-block;
        }

        .scrollable-carousel-list {
            display: flex;
            margin: 0 0 ${SCROLLBAR_HEIGHT};
            padding: 0;
            list-style: none;
        }

        .scrollable-carousel-item {
            padding: 0;
        }

        ${mobile(css`
            .scrollable-carousel-list {
                padding: 0 var(--layout-spacing-12) 0.5rem
                    var(--layout-spacing-12);
            }

            &::before,
            &::after {
                width: ${GRADIENT_WIDTH_MOBILE / 10}rem;
            }
        `)}
    `;
};

const buttonSizeStyles = ({ buttonSize }: StyledScrollableCarouselProps) => {
    switch (buttonSize) {
        case ScrollableCarouselButtonSize.SMALL:
            return css`
                .scrollable-carousel-arrow {
                    opacity: 1;
                    position: absolute;
                    padding: 1rem;
                    cursor: pointer;

                    &--previous,
                    &--next {
                        top: 0;
                        z-index: 5;
                        height: 100%;
                    }
                    &--previous {
                        left: 0;
                        transform: rotate(180deg);
                    }

                    &--next {
                        right: 0;
                    }
                }
            `;
        case ScrollableCarouselButtonSize.MEDIUM:
        default:
            return css`
                .scrollable-carousel-arrow {
                    opacity: 1;
                    position: absolute;
                    top: 50%;
                    z-index: 3;

                    /* Stop IconButton overwriting the box-shadow */
                    &:focus:not([disabled]) .icon {
                        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
                    }

                    .icon {
                        background: var(--colors-surface-primary);
                        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);

                        svg {
                            width: 2.4rem;
                            height: 2.4rem;
                        }
                    }

                    &--previous {
                        transform: translateY(-50%) rotate(180deg);
                        left: var(--layout-spacing-24);
                    }

                    &--next {
                        transform: translateY(-50%);
                        right: var(--layout-spacing-24);
                    }
                }
            `;
    }
};

/**
 * The ScrollableCarousel component styles
 *
 * @param {StyledScrollableCarouselProps} props The scrollable-carousel controller properties object
 */
export const styles = (props: StyledScrollableCarouselProps) => css`
    ${baseStyles(props)}
    ${buttonSizeStyles(props)}
`;
